/* eslint-disable max-lines-per-function */
import useAddEditStaffAugmentationState from './useAddEditStaffAugmentationState';
import UseAxios from '../../../Api/http/http';
import { getSkills } from '../../../Api/request/skills';
import { STAFF_AUGMENTATION } from '../../../Constants/strings';
import { useCallback, useState } from 'react';
import debounce from 'lodash.debounce';
import {
  fetchUserDataFromZoho,
  convertEmpStatusToValue,
} from '../../../Utils/userDataUtil';
import dayjs from 'dayjs';
import { logError } from '../../../Utils/logger';

const salesMemberDepartments = process.env.REACT_APP_SALES_MEMBER_DEPARTMENTS?.split(',') ;
const deliveryManagerDepartments = process.env.REACT_APP_DELIVERY_MANAGER_DEPARTMENTS?.split(',') ;

const defaultStaffData = {
  clientName: '',
  salesMember: '',
  deliveryManager: '',
  salesMemberId: '',
  deliveryManagerId: '',
  location: '',
  vertical: '',
  mandatoryFields: [],
  resourceOnboarded: '',
  optionalFields: '',
  interviewRequired: '',
  yearOfExperince: '',
  closureDate: null,
  duration: '',
  description: '',
  raisedDate: null,
  lostDroppedReason: '',
  jdReceived: '',
  dealType: '',
  status: '',
  uploadJd: '',
  noOfPositions: '',
  isTeamVisible: false,
};


const useAddEditStaffAugmentation = () => {
  const {
    data,
    skills,
    errors,
    openSnackbar,
    snackbar,
    setData,
    setSkills,
    setErrors,
    setOpenSnackbar,
    setSnackbar,
    yesNoOptions,
    dealTypeOptions,
    lostOrDroppedTypeOptions,
    styles,
  } = useAddEditStaffAugmentationState();

  const fieldsToShowMapEmp = [
    'employeeId',
    'fullName',
    'emailId',
    'employeestatus',
    'photoSignedUrl',
    'totalExperience',
  ];

  const [loadingSalesMembers, setLoadingSalesMembers] = useState(false);
  const [loadingDeliveryManagers, setLoadingDeliveryManagers] = useState(false);
  const [salesMemberOptions, setSalesMemberOptions] = useState([]);
  const [deliveryManagerOptions, setDeliveryManagerOptions] = useState([]);
  const [salesMemberId, setSalesMemberId] = useState('');
  const [deliveryManagerId, setDeliveryManagerId] = useState('');


  const debouncedFetchSalesMembers = useCallback(
    debounce(async (newInputValue) => {
      try {
        const employeeInfo = await fetchUserDataFromZoho(
          newInputValue,
          fieldsToShowMapEmp,
          salesMemberDepartments,
        );
        const activeEmployees = employeeInfo.filter(
          (employee) =>
            convertEmpStatusToValue(employee.employeestatus) === 1,
        );
        setLoadingSalesMembers(false);
        setSalesMemberOptions(activeEmployees);
        setSalesMemberId(activeEmployees[0]?.employeeId);
      } catch (error) {
        logError('Error fetching sales members: ' + error);
        setLoadingSalesMembers(false);
        setSalesMemberOptions([]);
      }
    }, 500), // 500 milliseconds delay
    [],
  );

  const debouncedFetchDeliveryManagers = useCallback(
    debounce(async (newInputValue) => {
      try {
        const employeeInfo = await fetchUserDataFromZoho(
          newInputValue,
          fieldsToShowMapEmp,
          deliveryManagerDepartments,
        );
        const activeEmployees = employeeInfo.filter(
          (employee) =>
            convertEmpStatusToValue(employee.employeestatus) === 1,
        );
        setLoadingDeliveryManagers(false);
        setDeliveryManagerOptions(activeEmployees);
        setDeliveryManagerId(activeEmployees[0]?.employeeId);
      } catch (error) {
        logError('Error fetching delivery managers: ' + error);
        setLoadingDeliveryManagers(false);
        setDeliveryManagerOptions([]);
      }
    }, 500), // 500 milliseconds delay
    [],
  );

  const handleSalesMemberChange = async (newInputValue) => {
    if (newInputValue && newInputValue?.length >= 3) {
      setLoadingSalesMembers(true);
      debouncedFetchSalesMembers(newInputValue);
    } else {
      setLoadingSalesMembers(false);
      setSalesMemberOptions([]);
    }
  };

  const handleDeliveryManagerChange = async (newInputValue) => {
    if (newInputValue?.length >= 3) {
      setLoadingDeliveryManagers(true);
      debouncedFetchDeliveryManagers(newInputValue);
    } else {
      setLoadingDeliveryManagers(false);
      setDeliveryManagerOptions([]);
    }
  };

  const clearForm = () => {
    setData({
      clientName: '',
      salesMember: '',
      deliveryManager: '',
      salesMemberId: '',
      deliveryManagerId: '',
      location: '',
      vertical: '',
      mandatoryFields: [],
      resourceOnboarded: '',
      optionalFields: '',
      interviewRequired: '',
      yearOfExperince: '',
      closureDate: null,
      duration: '',
      description: '',
      raisedDate: null,
      lostDroppedReason: '',
      jdReceived: '',
      status: '',
      uploadJd: '',
      dealType: '',
    });
    setErrors({});
  };
  const getSkillsData = (verticalId = undefined) => {
    UseAxios(getSkills({ isActiveFlag: true, verticalId }))
      .then((response) => {
        setSkills(
          response.data.skillDetails.map((skill) => ({
            id: skill.id,
            name: skill.skillName,
          })),
        );
      })
      .catch(() => {
        setSkills([]);
      });
  };

  const snackbarOpen = () => {
    setOpenSnackbar(true);
    setTimeout(() => {
      setOpenSnackbar(false);
    }, 4000);
  };

  const getFilteredMandatorySkills = () => {
    return skills?.filter((skill) => !data.optionalFields?.includes(skill));
  };

  const handleResetData = () => {
    setData(defaultStaffData);
    clearForm();
  };


  const handleDateValidation = (raisedDate, closureDate) => {
    const minDate = dayjs('2000-01-01');
    const maxDate = dayjs('2099-12-31');
    let raisedDateError = '';
    let closureDateError = '';

    // Validate raised date
    if (raisedDate) {
      const raisedDateObj = dayjs(raisedDate);
      if (raisedDateObj.isBefore(minDate) || raisedDateObj.isAfter(maxDate)) {
        raisedDateError = STAFF_AUGMENTATION.DATE_ERROR_MESSAGE;
      }
    }

    // Validate closure date
    if (closureDate) {
      const closureDateObj = dayjs(closureDate);
      if (closureDateObj.isBefore(minDate) || closureDateObj.isAfter(maxDate)) {
        closureDateError = STAFF_AUGMENTATION.DATE_ERROR_MESSAGE;
      }

      // Check if closure date is before raised date
      if (raisedDate && !raisedDateError && closureDateObj.isBefore(dayjs(raisedDate))) {
        closureDateError = STAFF_AUGMENTATION.CLOSURE_DATE_ERROR_MESSAGE;
      }
    }

    return { raisedDateError, closureDateError };
  };

  const handleRaisedDateChange = (event) => {
    const selectedDate = event ? dayjs(event) : null;

    if (!selectedDate?.isValid()) {
      setErrors(prev => ({
        ...prev,
        raisedDate: STAFF_AUGMENTATION.DATE_ERROR_MESSAGE,
      }));
      return;
    }

    setData(prev => ({ ...prev, raisedDate: selectedDate }));

    const { raisedDateError, closureDateError } = handleDateValidation(
      selectedDate,
      data?.closureDate,
    );

    setErrors(prev => ({
      ...prev,
      raisedDate: raisedDateError,
      closureDate: closureDateError,
    }));
  };

  const handleClosureDateChange = (event) => {
    const selectedDate = event ? dayjs(event) : null;

    if (!selectedDate) {
      setData(prev => ({ ...prev, closureDate: null })); // Set closureDate to null when cleared
      setErrors(prev => ({ ...prev, closureDate: '' }));
      return;
    }

    if (!selectedDate?.isValid()) {
      setErrors(prev => ({
        ...prev,
        closureDate: STAFF_AUGMENTATION.DATE_ERROR_MESSAGE,
      }));
      return;
    }

    setData(prev => ({ ...prev, closureDate: selectedDate }));

    const { raisedDateError, closureDateError } = handleDateValidation(
      data?.raisedDate,
      selectedDate,
    );

    setErrors(prev => ({
      ...prev,
      raisedDate: raisedDateError,
      closureDate: closureDateError,
    }));
  };

  const validateForm = () => {
    const newErrors = {};
    const requiredFields = [
      { field: data?.clientName, name: 'clientName' },
      { field: data?.salesMember, name: 'salesMember' },
      { field: data?.deliveryManager, name: 'deliveryManager' },
      { field: data?.location, name: 'location' },
      { field: data?.vertical, name: 'vertical' },
      { field: data?.mandatoryFields?.length, name: 'mandatoryFields' },
      { field: data?.optionalFields?.length, name: 'optionalFields' },
      { field: data?.resourceOnboarded, name: 'resourceOnboarded' },
      { field: data?.interviewRequired, name: 'interviewRequired' },
      { field: data?.minExperience, name: 'minExperience' },
      { field: data?.raisedDate, name: 'raisedDate' },
      { field: data?.jdReceived, name: 'jdReceived' },
      { field: data?.status, name: 'status' },
      { field: data?.dealType, name: 'dealType'},
      { field: data?.geography, name: 'geography'},
      { field: data?.noOfPositions, name: 'noOfPositions'},
    ];
    requiredFields.forEach(({ field, name }) => {
      if (name === 'clientName' && field?.length > 110) {
        newErrors[name] = STAFF_AUGMENTATION.CHARACTER_LIMIT_EXCEEDED;
      } else if (name === 'minExperience' && field?.length > 25) {
        newErrors[name] = STAFF_AUGMENTATION.CHARACTER_LIMIT_EXCEEDED;
      } else if (name === 'location' && field?.length > 64) {
        newErrors[name] = STAFF_AUGMENTATION.CHARACTER_LIMIT_EXCEEDED;
      } else if (name === 'geography' && !field) {
        newErrors[name] = STAFF_AUGMENTATION.GEOGRAPHY_REQUIRED;
      } else if (!field) {
        newErrors[name] = STAFF_AUGMENTATION.FILL_REQUIRED_FIELD;
      }
    });

    if (data?.description && data?.description?.length > 250) {
      newErrors['description']
      = STAFF_AUGMENTATION.DESCRIPTION_LENGTH_ERROR_MESSAGE;
    } else if (data?.description && data?.description?.length < 2) {
      newErrors['description']
      = STAFF_AUGMENTATION.DESCRIPTION_LINES_ERROR_MESSAGE;
    }

    if (data?.lostDroppedType && !data?.lostDroppedReason) {
      newErrors['lostDroppedReason'] = STAFF_AUGMENTATION.LOST_DROPPED_REASON_REQUIRED;
    }
    const noOfPositions = data?.noOfPositions ? Number(data?.noOfPositions) : '';
    if (noOfPositions === '') {
      newErrors['noOfPositions'] = STAFF_AUGMENTATION.FILL_REQUIRED_FIELD;
    } else if (isNaN(noOfPositions) || noOfPositions <= 0) {
      newErrors['noOfPositions'] = isNaN(noOfPositions)
        ? STAFF_AUGMENTATION.NO_OF_POSITIONS_CHAR_ERROR
        : STAFF_AUGMENTATION.NO_OF_POSITIONS_ZERO_ERROR;
    }
    if (data?.minExperience === undefined || data?.minExperience === '') {
      newErrors['minExperience'] = STAFF_AUGMENTATION.FILL_REQUIRED_FIELD;
    }
    if (data?.maxExperience || data?.minExperience) {
      const minExpRaw = data?.minExperience ?? '';
      const maxExpRaw = data?.maxExperience ?? '';

      const isDecimal = (val) => !Number.isInteger(Number(val));
      const isNegative = (val) => Number(val) < 0;

      if ((minExpRaw && (isDecimal(minExpRaw) || isNegative(minExpRaw)))
        || (maxExpRaw && (isDecimal(maxExpRaw) || isNegative(maxExpRaw)))) {
        newErrors['minExperience'] = STAFF_AUGMENTATION.INVALID_EXPERIENCE;
      }
      const minExp = parseInt(minExpRaw);
      const maxExp = parseInt(maxExpRaw);

      if (!isNaN(minExp) && !isNaN(maxExp) && minExp > maxExp) {
        newErrors['minExperience'] = STAFF_AUGMENTATION.MAX_EXPERIENCE_SHOULD_BE_GREATER;
      }
    }


    const { raisedDateError, closureDateError } = handleDateValidation(
      data?.raisedDate,
      data?.closureDate,
    );

    if (raisedDateError) newErrors.raisedDate = raisedDateError;
    if (closureDateError) newErrors.closureDate = closureDateError;


    setErrors(newErrors);

    // Return true if no errors, false otherwise
    return Object.keys(newErrors).length === 0;
  };

  const getUniqueSkills = (skillsData, skillNames) => {
    const seen = new Set();
    return skillsData
      ?.filter((skill) => skillNames?.includes(skill.skillName))
      ?.filter((skill) => {
        if (seen.has(skill.skillId)) {
          return false;
        }
        seen.add(skill.skillId);
        return true;
      })
      ?.map((skill) => ({ id: skill.skillId, name: skill.skillName }));
  };

  const handleAutoFieldChange = (field, valueOrEvent) => {
    const value = valueOrEvent?.target ? valueOrEvent.target.value : valueOrEvent;

    setData((prev) => ({
      ...prev,
      [field]: value,
    }));

    const newErrors = {};

    setErrors((prev) => {
      const rest = { ...prev };
      delete rest[field];
      return { ...rest, ...newErrors };
    });
  };

  return {
    salesMemberId,
    deliveryManagerId,
    data,
    skills,
    errors,
    openSnackbar,
    snackbar,
    setData,
    setSnackbar,
    setErrors,
    getSkillsData,
    getFilteredMandatorySkills,
    getUniqueSkills,
    snackbarOpen,
    handleResetData,
    validateForm,
    loadingSalesMembers,
    loadingDeliveryManagers,
    salesMemberOptions,
    deliveryManagerOptions,
    handleSalesMemberChange,
    handleDeliveryManagerChange,
    yesNoOptions,
    dealTypeOptions,
    lostOrDroppedTypeOptions,
    styles,
    handleRaisedDateChange,
    handleClosureDateChange,
    handleAutoFieldChange,
  };
};

export default useAddEditStaffAugmentation;
